import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import GoldButton from "../ui/GoldButton";
import { MessageCircle } from "lucide-react";
import { getWhatsAppUrl, inferWhatsAppContext } from "../../lib/whatsapp";

const megaMenu = {
  web: {
    title: "Web & Digital",
    items: [
      { label: "Website Development", href: "/services/web-development" },
      { label: "E-Commerce (Shopify/Custom)", href: "/services/ecommerce" },
      { label: "SEO & Organic Growth", href: "/services/seo" },
      { label: "Digital Marketing & Ads", href: "/services/digital-marketing" },
      { label: "Social Media Management", href: "/services/social-media" },
      { label: "Branding & Visual Identity", href: "/services/branding" },
      { label: "UI/UX Design", href: "/services/ui-ux" },
    ],
  },
  software: {
    title: "Software & Enterprise",
    items: [
      { label: "Custom CRM Development", href: "/services/crm-development" },
      { label: "Custom ERP Development", href: "/services/erp-development" },
      { label: "Web Application Development", href: "/services/web-apps" },
      { label: "Mobile App Development", href: "/services/mobile-apps" },
      { label: "Backend & API Development", href: "/services/api-development" },
      { label: "Cloud & DevOps", href: "/services/cloud-devops" },
      { label: "Integrations & Automation", href: "/services/integrations" },
    ],
  },
  ai: {
    title: "AI & Automation",
    items: [
      { label: "AI Agents & Chatbots", href: "/services/ai-agents" },
      { label: "AI Call Centre Solutions", href: "/services/ai-call-centre" },
      { label: "LLM Integration Services", href: "/services/llm-integration" },
      { label: "RAG Pipelines & Vector DBs", href: "/services/rag-pipelines" },
      { label: "RPA & Workflow Automation", href: "/services/rpa-automation" },
      { label: "Data, Analytics & BI", href: "/services/analytics" },
      { label: "AI Strategy Consulting", href: "/services/ai-consulting" },
    ],
  },
};

const navLinks = [
  { label: "Solutions", hasMega: true },
  { label: "Work", href: "/work" },
  { label: "Products", href: "/products" },
  { label: "Industries", href: "/industries" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const featuredLinks = [
  { label: "OWL CaseMind", sub: "AI insights for law firms", href: "/products/casemind" },
  { label: "Hospital Management", sub: "End-to-end medical ERP", href: "/products/hospital-management" },
  { label: "Appointment Bot", sub: "24/7 AI-driven scheduling", href: "/products/appointment-bot" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const megaCloseTimeoutRef = useRef<number | null>(null);
  const location = useLocation();
  const whatsappContext = inferWhatsAppContext(location.pathname);
  const whatsappHref = getWhatsAppUrl(whatsappContext);

  const clearMegaCloseTimeout = () => {
    if (megaCloseTimeoutRef.current !== null) {
      window.clearTimeout(megaCloseTimeoutRef.current);
      megaCloseTimeoutRef.current = null;
    }
  };

  const openMega = () => {
    clearMegaCloseTimeout();
    setMegaOpen(true);
  };

  const closeMegaWithDelay = () => {
    clearMegaCloseTimeout();
    megaCloseTimeoutRef.current = window.setTimeout(() => {
      setMegaOpen(false);
      megaCloseTimeoutRef.current = null;
    }, 140);
  };

  useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 80);
          ticking = false;
        });
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
    clearMegaCloseTimeout();
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      clearMegaCloseTimeout();
    };
  }, []);

  return (
    <>
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 clamp(20px, 4vw, 40px)",
          background: scrolled ? "rgba(5, 5, 5, 0.98)" : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(212,168,67,0.12)" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <picture>
            <source srcSet="/owl-logo.webp" type="image/webp" />
            <img src="/owl-logo.png" alt="OWL" width={42} height={42} style={{ height: 42, width: 42, objectFit: "contain" }} />
          </picture>
          <span
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: "0.25em",
              color: "#D4A843",
              textTransform: "uppercase",
            }}
          >
            Obsidian Web Labs
          </span>
        </Link>

        <div style={{ display: "flex", alignItems: "center", gap: 28 }} className="desktop-nav">
          {navLinks.map((link) =>
            link.hasMega ? (
              <div
                key={link.label}
                onMouseEnter={openMega}
                onMouseLeave={closeMegaWithDelay}
                style={{ position: "relative" }}
              >
                <button
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 400,
                    fontSize: 16,
                    color: megaOpen ? "#D4A843" : "#F8F4EC",
                    transition: "color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {link.label}
                  <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
                </button>

                {megaOpen && (
                  <div
                    onMouseEnter={openMega}
                    onMouseLeave={closeMegaWithDelay}
                    style={{
                      position: "fixed",
                      top: 72,
                      left: 0,
                      right: 0,
                      background: "#0E0E0E",
                      borderTop: "1px solid rgba(212,168,67,0.15)",
                      borderBottom: "1px solid rgba(212,168,67,0.15)",
                      padding: "40px clamp(20px, 4vw, 40px)",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 280px",
                      gap: 40,
                      zIndex: 999,
                    }}
                  >
                    {Object.values(megaMenu).map((column) => (
                      <div key={column.title}>
                        <div
                          style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: "0.3em",
                            color: "#D4A843",
                            textTransform: "uppercase",
                            marginBottom: 18,
                            paddingBottom: 12,
                            borderBottom: "0.5px solid rgba(212,168,67,0.2)",
                          }}
                        >
                          {column.title}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {column.items.map((item) => (
                            <Link
                              key={item.label}
                              to={item.href}
                              style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 300,
                                fontSize: 14,
                                color: "#908A83",
                                padding: "6px 0",
                                transition: "color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#F8F4EC";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#908A83";
                              }}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div
                      style={{
                        background: "#1A1208",
                        border: "1px solid rgba(212,168,67,0.2)",
                        borderRadius: 4,
                        padding: 24,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "'Barlow Condensed', sans-serif",
                          fontWeight: 700,
                          fontSize: 11,
                          letterSpacing: "0.3em",
                          color: "#D4A843",
                          marginBottom: 20,
                          textTransform: "uppercase",
                        }}
                      >
                        Featured
                      </div>
                      {featuredLinks.map((feature) => (
                        <Link
                          key={feature.label}
                          to={feature.href}
                          style={{ display: "block", marginBottom: 16, textDecoration: "none" }}
                          onMouseEnter={(e) => {
                            const title = e.currentTarget.querySelector(".feature-title");
                            if (title instanceof HTMLElement) {
                              title.style.color = "#D4A843";
                            }
                          }}
                          onMouseLeave={(e) => {
                            const title = e.currentTarget.querySelector(".feature-title");
                            if (title instanceof HTMLElement) {
                              title.style.color = "#F8F4EC";
                            }
                          }}
                        >
                          <span
                            className="feature-title"
                            style={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontWeight: 500,
                              fontSize: 14,
                              color: "#F8F4EC",
                              display: "block",
                              transition: "color 0.2s",
                            }}
                          >
                            {feature.label}
                          </span>
                          <span
                            style={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontWeight: 300,
                              fontSize: 12,
                              color: "#908A83",
                            }}
                          >
                            {feature.sub}
                          </span>
                        </Link>
                      ))}
                      <Link
                        to="/products"
                        style={{
                          display: "block",
                          marginTop: 20,
                          fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: 13,
                          letterSpacing: "0.2em",
                          color: "#D4A843",
                          borderTop: "0.5px solid rgba(212,168,67,0.2)",
                          paddingTop: 16,
                          textDecoration: "none",
                        }}
                      >
                        EXPLORE ALL PRODUCTS →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href || "/"}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 400,
                  fontSize: 16,
                  color: "#F8F4EC",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#D4A843";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#F8F4EC";
                }}
              >
                {link.label}
              </Link>
            )
          )}
          <GoldButton href="/contact" size="sm">
            Get Started
          </GoldButton>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "1px solid rgba(37,211,102,0.45)",
              color: "#25D366",
            }}
            aria-label="WhatsApp"
          >
            <MessageCircle size={16} />
          </a>
        </div>

        <button
          onClick={() => setMobileOpen((value) => !value)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "none",
            flexDirection: "column",
            gap: 5,
          }}
          className="mobile-hamburger"
          aria-label="Toggle navigation"
        >
          {[0, 1, 2].map((index) => (
            <span key={index} style={{ display: "block", width: 24, height: 1.5, background: "#D4A843" }} />
          ))}
        </button>
      </nav>

      {mobileOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#050505",
            zIndex: 999,
            padding: "90px clamp(20px, 5vw, 32px) 32px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            overflowY: "auto",
          }}
        >
          <button
            onClick={() => setMobileOpen(false)}
            style={{
              position: "absolute",
              top: 24,
              right: 24,
              background: "none",
              border: "none",
              color: "#D4A843",
              fontSize: 28,
              cursor: "pointer",
              lineHeight: 1,
              padding: "4px 8px",
            }}
            aria-label="Close menu"
          >
            ×
          </button>

          {navLinks.map((link) =>
            link.hasMega ? (
              <div key={link.label}>
                <button
                  onClick={() => setMobileSolutionsOpen((v) => !v)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 32,
                    letterSpacing: "0.05em",
                    color: "#F8F4EC",
                    borderBottom: mobileSolutionsOpen ? "none" : "0.5px solid rgba(212,168,67,0.12)",
                    padding: "20px 0",
                  }}
                >
                  {link.label}
                  <span style={{ fontSize: 16, color: "#D4A843", transition: "transform 0.2s", transform: mobileSolutionsOpen ? "rotate(180deg)" : "none" }}>▾</span>
                </button>

                {mobileSolutionsOpen && (
                  <div
                    style={{
                      borderBottom: "0.5px solid rgba(212,168,67,0.12)",
                      paddingBottom: 16,
                      marginBottom: 4,
                    }}
                  >
                    {Object.values(megaMenu).map((column) => (
                      <div key={column.title} style={{ marginBottom: 20 }}>
                        <div
                          style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontWeight: 700,
                            fontSize: 10,
                            letterSpacing: "0.3em",
                            color: "#D4A843",
                            textTransform: "uppercase",
                            marginBottom: 10,
                            paddingTop: 12,
                          }}
                        >
                          {column.title}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {column.items.map((item) => (
                            <Link
                              key={item.label}
                              to={item.href}
                              style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 300,
                                fontSize: 15,
                                color: "#8B8378",
                                padding: "7px 0",
                                display: "block",
                              }}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Link
                      to="/products"
                      style={{
                        display: "inline-block",
                        marginTop: 8,
                        fontFamily: "'Bebas Neue', sans-serif",
                        fontSize: 13,
                        letterSpacing: "0.2em",
                        color: "#D4A843",
                        paddingTop: 12,
                        borderTop: "0.5px solid rgba(212,168,67,0.2)",
                        width: "100%",
                      }}
                    >
                      EXPLORE ALL PRODUCTS →
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href || "/"}
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 32,
                  letterSpacing: "0.05em",
                  color: "#F8F4EC",
                  borderBottom: "0.5px solid rgba(212,168,67,0.12)",
                  padding: "20px 0",
                  display: "block",
                }}
              >
                {link.label}
              </Link>
            )
          )}

          <div style={{ marginTop: 32 }}>
            <GoldButton href="/contact" fullWidth>
              Get Started
            </GoldButton>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-hamburger { display: flex !important; }
        }
      `}</style>
    </>
  );
}
